import { useCoreTheme } from "hooks/use-wireapp-theme";
import React from "react";
import type { CoreTheme } from "sharedUI/styles";
import { coreTheme } from "sharedUI/styles";
import { ThemeProvider } from "styled-components";

interface Props {
    theme: CoreTheme;
    setPortal?: boolean;
}

function keyToVariableName(str: string): string {
    return `--gv-${str.replace(/[a-z][A-Z]/g, x =>
        x
            .split("")
            .map(c => c.toLowerCase())
            .join("-")
    )}`;
}

function setAttributes(portal: Element, css: Record<string, string>): void {
    const result = Object.keys(css)
        .map(key => `${key}:${css[key]}`)
        .join("; ");
    portal.setAttribute("style", result);
}

const keys = Object.keys(coreTheme) as (keyof CoreTheme)[];
export const TailwindThemeProvider: React.FC<Props> = p => {
    const { theme, setPortal } = p;
    // the type we get is actually incorrect and the top level ThemeProvider doesn't have a parent
    // so it will get an undefined.
    const parentTheme = useCoreTheme() as CoreTheme | undefined;

    const [css, portalCss] = React.useMemo(() => {
        const vars: Record<string, string> = {};
        const forPortal: Record<string, string> = {};
        for (const key of keys) {
            if (typeof theme[key] !== "string") continue;
            if (theme[key] !== parentTheme?.[key]) {
                vars[keyToVariableName(key)] = theme[key];
            }
            if (setPortal) {
                forPortal[keyToVariableName(key)] = theme[key];
            }
        }
        return [vars, forPortal];
    }, [parentTheme, setPortal, theme]);

    if (setPortal) {
        const portal = document.getElementById("portal") as Element;
        if (portal !== null) {
            setAttributes(portal, portalCss);
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <div style={css}>{p.children}</div>
        </ThemeProvider>
    );
};
